import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import {
  UntypedFormGroup,
  UntypedFormControl,
  UntypedFormBuilder,
  Validators,
} from "@angular/forms";
import { GlobalValidators } from "./../../shared/validators/global-validators";
import { RegexPatterns } from "./../../shared/regex/regex-patterns";
import { EqualsValidatorDirective } from "./../../shared/directives/equals-validator.directive";

import { NotificationsService } from "./../../shared/services/notifications.service";
import { ViewStateService } from "./../../shared/services/view-state.service";
import { CommonApiService } from "./../../shared/services/api/common-api.service";
import { AppSettingsService } from "./../../shared/services/app-settings.service";
import { AuthService } from "./../auth.service";
import moment from "moment";
import { filter, takeUntil } from "rxjs/operators";
import { forkJoin, Subject } from "rxjs";

@Component({
  selector: "app-create-profile",
  templateUrl: "./create-profile.component.html",
  styleUrls: ["./create-profile.component.css"],
})
export class CreateProfileComponent implements OnInit, OnDestroy {
  private dateInputFormat = this.appSettings.IsCompatibilityMode
    ? "MM/DD/YYYY"
    : "YYYY-MM-DD";
  private destroy$ = new Subject<void>();

  studentForm: UntypedFormGroup;
  formLevelErrors = [];
  disciplineLov: any[];
  genderLov: any[];
  partialProfile: boolean = false;
  successfulSubmission: boolean = false;
  countries: any;
  studentId: string;
  badZipMessage = false;
  schoolCode: any;
  schoolName: any;
  alreadyComplete = false;
  loginError: any;
  personDiscipline: any;
  disciplineAudit: boolean = false;

  showPassword: boolean = false;
  showPasswordConfirmation: boolean = false;

  mxd = new Date();
  mnd = new Date();
  minDate: Date = new Date(this.mnd.setFullYear(this.mnd.getFullYear() - 110));
  maxDate: Date = new Date(this.mxd.setFullYear(this.mxd.getFullYear() - 18));

  constructor(
    private formBuilder: UntypedFormBuilder,
    private commonApiService: CommonApiService,
    private viewState: ViewStateService,
    private router: Router,
    private notifications: NotificationsService,
    public appSettings: AppSettingsService,
    public authService: AuthService
  ) {}

  ngOnInit() {
    this.viewState.startLoading();
    this.getLov();
    this.buildForm();
    this.setQueryParams();
    this.retrieveCommonData();
    this.studentForm.get("countryId").patchValue(1);
  }

  get validDiscipline() {
    if (
      this.studentForm.get("discipline").value == "" ||
      this.studentForm.get("discipline").value == "N" || //other
      this.studentForm.get("discipline").value == "30" || //1000 hour
      this.studentForm.get("discipline").value == "4" || //rt
      this.studentForm.get("discipline").value == "5" || //pft
      this.studentForm.get("discipline").value == "S"
    )
      //student
      return false;
    else return true;
  }

  get disciplinePicked() {
    return this.studentForm.get("discipline").value;
  }

  setQueryParams() {
    let ssn = this.router.routerState.snapshot.root.queryParams["ssn"];
    if (ssn) this.studentForm.get("ssn").patchValue(ssn);
    let email = this.router.routerState.snapshot.root.queryParams["email"];
    if (email) this.studentForm.get("email").patchValue(email);
  }

  get selectedCountry() {
    return this.studentForm.get("countryId").value;
  }
  get isUs() {
    return this.selectedCountry == 1;
  }
  onSubmit(updatedFormValues: any) {
    this.viewState.startLoading();
    this.validateForm();
    this.save(updatedFormValues);
  }

  getLov() {
    forkJoin({
      discipline: this.commonApiService.getLov("DISCIPLINE"),
      gender: this.commonApiService.getLov("GENDER"),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ discipline, gender }) => {
          this.disciplineLov = discipline;
          this.genderLov = gender;
        },
        error: (err) => {
          console.log(err);
        },
      });
  }

  performZipCodeLookup() {
    this.badZipMessage = false;
    const zip = this.studentForm.value.postalCode
      ? this.studentForm.value.postalCode.split("-")
      : "";
    if (!zip) return;

    this.commonApiService
      .lookupStateAndCityByZipCode(zip[0])
      .pipe(
        filter((res) => res.length !== 0),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (val) => {
          const value = val[0] || "";
          if (value) {
            this.setFormValue(value);
          }
        },
        error: (error) => {
          if(this.studentForm.value.countryId == 1) {
            this.badZipMessage = true;
            console.log(error);
          }
        },
      });
  }

  private setFormValue(lookUpValues) {
    this.studentForm.patchValue({
      state: lookUpValues.stateCode,
      city: lookUpValues.city,
    });
  }

  private validateForm() {
    for (const control in this.studentForm.controls) {
      this.studentForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.studentForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }
    if (hasErrors) {
      this.notifications.error(
        "Fields Invalid",
        "Please review the form and resubmit."
      );
      this.viewState.stopLoading();
    }
  }

  private buildForm(): void {
    let mnd = new Date();
    let mxd = new Date();
    this.minDate = new Date(mnd.setFullYear(mnd.getFullYear() - 110));
    this.maxDate = new Date(mxd.setFullYear(mxd.getFullYear() - 18));

    this.studentForm = this.formBuilder.group({
      id: [this.formValues.id],
      ssn: [
        this.formValues.ssn,
        [
          Validators.required,
          Validators.maxLength(9),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      firstName: [
        this.formValues.firstName,
        [
          Validators.required,
          Validators.maxLength(30),
          Validators.pattern(RegexPatterns.lettersOnly),
        ],
      ],
      middleName: [
        this.formValues.middleName,
        [
          Validators.maxLength(30),
          Validators.pattern(RegexPatterns.lettersOnly),
        ],
      ],
      lastName: [
        this.formValues.lastName,
        [
          Validators.required,
          Validators.maxLength(30),
          Validators.pattern(RegexPatterns.lettersOnly),
        ],
      ],
      suffix: [this.formValues.suffix],
      email: [
        this.formValues.email,
        [Validators.required, Validators.maxLength(100), Validators.email],
      ],
      gender: [this.formValues.gender, Validators.required],
      countryId: [
        this.formValues.countryId,
        [Validators.required, Validators.maxLength(40)],
      ],
      address1: [
        this.formValues.address1,
        [
          Validators.required,
          Validators.maxLength(35),
          Validators.pattern(RegexPatterns.alphaNumericSpecial),
        ],
      ],
      address2: [
        this.formValues.address2,
        [
          Validators.maxLength(35),
          Validators.pattern(RegexPatterns.alphaNumericSpecial),
        ],
      ],
      postalCode: [
        this.formValues.postalCode,
        [
          Validators.required,
          Validators.maxLength(10),
          //Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      city: [
        this.formValues.city,
        [
          Validators.required,
          Validators.maxLength(35),
          Validators.pattern(RegexPatterns.lettersOnly),
        ],
      ],
      state: [
        this.formValues.state,
        [Validators.required, Validators.maxLength(50)],
      ],
      homePhone: [
        this.formValues.homePhone,
        [
          Validators.maxLength(50),
          Validators.minLength(10),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      cellPhone: [
        this.formValues.cellPhone,
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.minLength(10),
          Validators.pattern(RegexPatterns.integersOnly),
        ],
      ],
      birthday: [
        this.formValues.birthday,
        [
          GlobalValidators.validDate(this.dateInputFormat),
          GlobalValidators.minDate(this.minDate),
          GlobalValidators.maxDate(this.maxDate),
          Validators.required,
        ],
      ],
      discipline: [this.formValues.discipline, [Validators.required]],
      password: [
        this.formValues.password,
        [Validators.required, this.passwordComplexity],
      ],
      passwordConfirmation: [
        this.formValues.passwordConfirmation,
        [Validators.required],
      ],
    });
  }

  private save(formValues) {
    if (this.studentForm.valid) {
      this.authService
        .createProfile(formValues)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (resp) => {
            this.viewState.stopLoading();
            this.personDiscipline = resp;
            this.successfulSubmission = true;
            if (this.personDiscipline && this.personDiscipline.auditRequired) {
              this.notifications.success(
                "Success",
                "The profile has been created."
              );
              this.disciplineAudit = true;
            } else {
              this.notifications.success(
                "Success",
                "The profile has been created"
              );

              this.authService
                .signIn(formValues.email, formValues.password, null, null)
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                  (response) => {
                    this.viewState.stopLoading();
                    this.router.navigate(["/"]);
                  },
                  (e) => {
                    const r = e;
                    if (r && r.message == "JE") {
                      this.loginError = this.validationMessages.je;
                    } else if (r && r.message == "LOCKED") {
                      this.loginError = this.validationMessages.locked;
                    } else {
                      this.loginError = this.validationMessages.login;
                    }
                    this.viewState.stopLoading();
                  }
                );
            }
          },
          (error) => {
            this.viewState.stopLoading();
            this.handleServerErrorResponse(error);
            console.error(error);
          }
        );
    } else {
      this.notifications.error(
        "Profile not saved",
        "Correct errors and resubmit"
      );
      this.viewState.stopLoading();
    }
  }

  continue() {
    let email = this.studentForm.value.email;
    let password = this.studentForm.value.password;
    this.authService
      .signIn(email, password, null, null)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          this.viewState.stopLoading();
          this.router.navigate(["/"]);
        },
        (e) => {
          const r = e;
          if (r && r.message == "JE") {
            this.loginError = this.validationMessages.je;
          } else if (r && r.message == "LOCKED") {
            this.loginError = this.validationMessages.locked;
          } else {
            this.loginError = this.validationMessages.login;
          }
          this.viewState.stopLoading();
        }
      );
  }

  private handleServerErrorResponse(response) {
    if (response.status === 500)
      this.notifications.error(
        "Unexpected Error",
        "An unexpected error occurred."
      );
    else this.mapServerErrorsToForm(response.error);

    this.viewState.stopLoading();
  }

  private retrieveCommonData() {
    this.commonApiService
      .getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.countries = res;
          this.viewState.stopLoading();
        },
        (error) => {
          console.log(error);
        }
      );
  }

  private mapServerErrorsToForm(errors) {
    let unknownErrors = [];
    for (let error of errors) {
      this.formErrors[error.fieldName] = "";
      const control = this.studentForm.get(error.fieldName);
      this.formErrors[error.fieldName] += error.message + " ";
      if (!control) unknownErrors.push(error.message);
    }

    if (unknownErrors.length > 0)
      this.notifications.error(
        "Form Errors",
        "Please check the form for invalid values."
      );
    else
      this.notifications.error(
        "Unexpected Errors",
        "If these errors persist, please contact NBRC for assistance."
      );

    this.viewState.stopLoading();
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  toggleShowPasswordConfirmation() {
    this.showPasswordConfirmation = !this.showPasswordConfirmation;
  }

  private formValues = {
    id: "",
    ssn: "",
    firstName: "",
    middleName: "",
    lastName: "",
    suffix: "",
    email: "",
    gender: "",
    countryId: "",
    address1: "",
    address2: "",
    postalCode: "",
    city: "",
    state: "",
    homePhone: "",
    cellPhone: "",
    birthday: "",
    discipline: "",
    password: "",
    passwordConfirmation: "",
  };

  formErrors = {
    ssn: "",
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    gender: "",
    countryId: "",
    address1: "",
    address2: "",
    postalCode: "",
    city: "",
    state: "",
    homePhone: "",
    cellPhone: "",
    birthday: "",
    discipline: "",
    password: "",
    passwordConfirmation: "",
  };

  validationMessages = {
    ssn: {
      required: "SSN is required.",
      maxlength: "SSN must be max 9 characters.",
      pattern:
        'SSN must be all numeric. If you have been assigned an "SSN" prefixed with letters please contact the NBRC to setup your account',
    },
    firstName: {
      required: "First Name is required.",
      maxlength: "First Name must be max 30 characters.",
      pattern: "First Name must be letters only.",
    },
    middleName: {
      maxlength: "Middle Name must be max 30 characters.",
      pattern: "Middle Name must be letters only.",
    },
    lastName: {
      required: "Last Name is required.",
      maxlength: "Last Name must be max 30 characters.",
      pattern: "Last Name must be letters only.",
    },
    email: {
      required: "Email is required.",
      maxlength: "Email must be max 100 characters.",
      email: "Email must be in the correct format.",
    },
    gender: {
      required: "Gender is required.",
    },
    countryId: {
      required: "Country is required.",
      maxlength: "Country must be max 40 characters.",
      pattern: "Country must be letters only.",
    },
    address1: {
      required: "Address is required.",
      maxlength: "Address must be max 35 characters.",
      pattern: "Address must be letters & numbers.",
    },
    address2: {
      maxlength: "Address must be max 35 characters.",
      pattern: "Address must be letters & numbers.",
    },
    postalCode: {
      required: "Zip / Postal Code is required.",
      maxlength: "Zip / Postal Code must be max 10 characters.",
      pattern: "Zip / Postal Code must be numbers only.",
    },
    city: {
      required: "City is required.",
      maxlength: "City must be max 35 characters.",
      pattern: "City must be letters only.",
    },
    state: {
      required: "State is required.",
      maxlength: "State must be max 50 characters.",
    },
    homePhone: {
      required: "Home Phone is required.",
      maxlength: "Home Phone must be max 10 characters.",
      minlength: "Home Phone must be minimum 10 characters.",
      pattern: "Home Phone must be numbers only.",
    },
    cellPhone: {
      required: "Cell Phone is required.",
      maxlength: "Cell Phone must be max 10 characters.",
      minlength: "Cell Phone must be minimum 10 characters.",
      pattern: "Cell Phone must be numbers only.",
    },
    discipline: {
      required: "Discipline is required.",
    },
    birthday: {
      required: "Date of birth required.",
      validDate: "Date of birth must be a valid date.",
      maxDate:
        "Date of birth must be at least 18 years ago and not in the future.",
      minDate: "Date of birth must be greater than {minDate}.".replace(
        "{minDate}",
        moment(this.minDate).format("MMM DD, YYYY")
      ),
    },
    password: {
      required: "Password is required.",
      passwordComplexity:
        "Password must meet complexity requirements (one number, one uppercase letter, one symbol, 8 characters).",
    },
    passwordConfirmation: {
      required: "Password Confirmation is required.",
      validateEqual: "Passwords do not match.",
    },
    login: "Email and/or password is incorrect.",
    je: "Login Locked out. Contact NBRC",
    locked: "Too many bad attempts. Account login locked for 30 minutes.",
  };

  private passwordComplexity(c: UntypedFormControl) {
    if (!c.value) return null;

    var valid = true;
    if (!c.value.match(/\d/)) valid = false;
    if (!c.value.match(/[A-Z]/)) valid = false;
    if (!c.value.match(/[a-z]/)) valid = false;
    if (!c.value.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/)) valid = false;
    if (!c.value.match(/^.{8,255}$/)) valid = false;

    if (valid) return null;

    return { passwordComplexity: { valid: "invalid" } };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
